import React, { useState } from 'react';
import FormBase from '../../components/forms/FormBase';
import FormField from '../../components/forms/FormField';
import IdentificacaoMunicipio from '../../components/forms/infraestrutura-economica-urbana/01_IdentificacaoMunicipio';
import BaseEconomica from '../../components/forms/infraestrutura-economica-urbana/02_BaseEconomica';
import InfraestruturaProdutiva from '../../components/forms/infraestrutura-economica-urbana/03_InfraestruturaProdutiva';
import ConectividadeLogistica from '../../components/forms/infraestrutura-economica-urbana/04_ConectividadeLogistica';
import InfraestruturaBasica from '../../components/forms/infraestrutura-economica-urbana/05_InfraestruturaBasica';
import MobilidadeUrbana from '../../components/forms/infraestrutura-economica-urbana/06_MobilidadeUrbana';
import HabitacaoPlanejamento from '../../components/forms/infraestrutura-economica-urbana/07_HabitacaoPlanejamento';
import IndicadoresSustentabilidade from '../../components/forms/infraestrutura-economica-urbana/08_IndicadoresSustentabilidade';
import GovernancaInvestimentos from '../../components/forms/infraestrutura-economica-urbana/09_GovernancaInvestimentos';
import ObservacoesDiagnostico from '../../components/forms/infraestrutura-economica-urbana/10_ObservacoesDiagnostico';
import { FiInfo, FiPlus, FiMinus } from 'react-icons/fi';
import { FormAccessGuard } from '../../hooks/useFormEnabled';
import { FORMS_BY_ID } from '../../config/forms';

const InfraestruturaEconomicaUrbana = () => {
  const [repeatableFields, setRepeatableFields] = useState({
    incentivo: [{ id: 1 }],
    projeto: [{ id: 1 }],
    ppp: [{ id: 1 }]
  });

  const addRepeatableField = (fieldType) => {
    setRepeatableFields(prev => {
      const newId = prev[fieldType].length > 0 
        ? Math.max(...prev[fieldType].map(item => item.id)) + 1 
        : 1;
      return {
        ...prev,
        [fieldType]: [...prev[fieldType], { id: newId }]
      };
    });
  };

  const removeRepeatableField = (fieldType, id) => {
    setRepeatableFields(prev => ({
      ...prev,
      [fieldType]: prev[fieldType].filter(item => item.id !== id)
    }));
  };

  const sections = [
    {
      title: '1. Identificação do Município',
      fields: [
        'nome_municipio',
        'codigo_ibge',
        'fontes_principais_secao1',
        'links_acesso_secao1',
      ],
    },
    {
      title: '2. Base Econômica',
      fields: [
        'setores_economicos',
        'setores_economicos_outro',
        'pib_municipal',
        'participacao_pib',
        'exportacoes_importacoes',
        'numero_empresas',
        'microempresas_percentual',
        'pequenas_empresas_percentual',
        'medias_grandes_empresas_percentual',
        'startups_presenca',
        'startups_setores',
        'fontes_principais_secao2',
        'links_acesso_secao2',
      ],
    },
    {
      title: '3. Infraestrutura Produtiva',
      fields: [
        'parques_industriais_numero',
        'parques_industriais_setores',
        'parques_industriais_ocupacao',
        'apl_quantidade',
        'apl_setores',
        'incentivos_fiscais',
        ...repeatableFields.incentivo.flatMap(item => [
          `incentivo_descricao_${item.id}`
        ]),
        'fontes_principais_secao3',
        'links_acesso_secao3',
      ],
    },
    {
      title: '4. Conectividade e Logística',
      fields: [
        'modal_predominante',
        'rodovias_condicao',
        'centros_logisticos_quantidade',
        'centros_logisticos_localizacao',
        'internet_cobertura',
        'fontes_principais_secao4',
        'links_acesso_secao4',
      ],
    },
    {
      title: '5. Infraestrutura Básica',
      fields: [
        'agua_abastecimento',
        'esgoto_cobertura',
        'residuos_coleta',
        'energia_cobertura',
        'energia_qualidade',
        'iluminacao_cobertura',
        'iluminacao_sustentavel',
        'fontes_principais_secao5',
        'links_acesso_secao5',
      ],
    },
    {
      title: '6. Mobilidade Urbana',
      fields: [
        'transporte_publico_tipos',
        'transporte_publico_extensao',
        'ciclovias_extensao',
        'mobilidade_sustentavel',
        'malha_viaria_condicao',
        'fontes_principais_secao6',
        'links_acesso_secao6',
      ],
    },
    {
      title: '7. Habitação e Planejamento Urbano',
      fields: [
        'deficit_habitacional',
        'habitacao_social',
        'plano_diretor_ano',
        'zoneamento_existencia',
        'drenagem_urbana',
        'areas_verdes',
        'espaco_publico',
        'fontes_principais_secao7',
        'links_acesso_secao7',
      ],
    },
    {
      title: '8. Indicadores de Sustentabilidade',
      fields: [
        'projetos_renovaveis',
        ...repeatableFields.projeto.flatMap(item => [
          `projeto_capacidade_${item.id}`
        ]),
        'reciclagem_taxa',
        'aterros_sanitarios',
        'poluicao_ar',
        'recursos_hidricos',
        'fontes_principais_secao8',
        'links_acesso_secao8',
      ],
    },
    {
      title: '9. Governança e Investimentos',
      fields: [
        'investimento_capacidade',
        ...repeatableFields.ppp.flatMap(item => [
          `ppp_exemplo_${item.id}`
        ]),
        'orcamento_participativo',
        'fontes_principais_secao9',
        'links_acesso_secao9',
      ],
    },
    {
      title: '10. Observações e Diagnóstico Preliminar',
      fields: [
        'pontos_fortes',
        'desafios_principais',
        'sugestoes_politicas',
        'fontes_principais_secao10',
        'links_acesso_secao10',
      ],
    },
  ];

  return (
    <FormBase
      formType="infraestrutura-economica-urbana"
      formTitle="Formulário - Políticas Públicas na RIDE-DF (Infraestrutura Econômica e Urbana)"
      formDescription="Preencha os dados sobre infraestrutura econômica e urbana na região da RIDE-DF. Os dados coletados serão utilizados para análises e estudos do Observatório Socioeconômico e Ambiental."
      sections={sections}
    >
      {/* Seção 1: Identificação do Município */}
      <IdentificacaoMunicipio />

      {/* Seção 2: Base Econômica */}
      <BaseEconomica />

      {/* Seção 3: Infraestrutura Produtiva */}
      <InfraestruturaProdutiva
        repeatableFields={repeatableFields}
        addRepeatableField={addRepeatableField}
        removeRepeatableField={removeRepeatableField}
      />

      {/* Seção 4: Conectividade e Logística */}
      <ConectividadeLogistica />

      {/* Seção 5: Infraestrutura Básica */}
      <InfraestruturaBasica />

      {/* Seção 6: Mobilidade Urbana */}
      <MobilidadeUrbana />

      {/* Seção 7: Habitação e Planejamento Urbano */}
      <HabitacaoPlanejamento />

      {/* Seção 8: Indicadores de Sustentabilidade */}
      <IndicadoresSustentabilidade
        repeatableFields={repeatableFields}
        addRepeatableField={addRepeatableField}
        removeRepeatableField={removeRepeatableField}
      />

      {/* Seção 9: Governança e Investimentos */}
      <GovernancaInvestimentos
        repeatableFields={repeatableFields}
        addRepeatableField={addRepeatableField}
        removeRepeatableField={removeRepeatableField}
      />

      {/* Seção 10: Observações e Diagnóstico Preliminar */}
      <ObservacoesDiagnostico />
      
    </FormBase>
  );
};

const InfraestruturaEconomicaUrbanaGuarded = () => (
  <FormAccessGuard formId="infraestrutura-economica-urbana" formTitle={FORMS_BY_ID['infraestrutura-economica-urbana'].title}>
    <InfraestruturaEconomicaUrbana />
  </FormAccessGuard>
);

export default InfraestruturaEconomicaUrbanaGuarded;
